import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { Article } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import UserSelect from '../../../molecules/UserSelect';
import useLocalization from '../../../../hooks/useLocalization';
import type { NeobetBetslipDrawerController } from '../../../../hooks/components/useNeobetBetslipDrawer';
import NeobetBetslipMenu, { NeobetBetslipMenuTriggerValue } from './NeobetBetslipMenu';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { FormattedBalance } from '../../../atoms/FormattedBalance';
import { WALLET_TYPE } from '../../../../types/enums';
import { useBetslipFlyAnimation } from '../../../../hooks/useBetslipFlyAnimation';
import { styles as headerStyles } from './Header.styles';

const styles = {
  headerTopRow: {
    px: 1.5,
    py: 1,
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'primary.main',
    color: 'background.default',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  headerTitleRow: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  headerMenu: {
    minWidth: 0,
    width: '100%',
    height: 28,
  },
  controlsSection: {
    px: 1.25,
    pt: 1,
    pb: 0.75,
    backgroundColor: 'background.paper',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  controlsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 0.75,
    alignItems: 'center',
  },
  controlRow: {
    minWidth: 0,
  },
  menuIconSelect: {
    width: 44,
    minWidth: 44,
    flexShrink: 0,
  },
  walletTriggerBalance: {
    typography: 'body4',
    fontWeight: 800,
    ml: 'auto',
    flexShrink: 0,
    color: 'text.primary',
  },
  bankerToggle: (active: boolean) => ({
    width: 28,
    minWidth: 28,
    height: 28,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: active ? 'primary.main' : 'divider',
    backgroundColor: active ? 'primary.main' : 'neutral.600',
    color: active ? 'background.default' : 'text.secondary',
    fontWeight: 800,
    flexShrink: 0,
    fontSize: 14,
    '&:hover': {
      backgroundColor: active ? 'primary.dark' : 'neutral.500',
    },
    '&:disabled': {
      opacity: 0.4,
      borderColor: 'divider',
      backgroundColor: 'neutral.600',
      color: 'text.secondary',
    },
  }),
  headerCloseButton: {
    color: 'primary.900',
    flexShrink: 0,
    ml: 'auto',
  },
  headerTriggerIcon: {
    color: 'primary.main',
    fontSize: 16,
    flexShrink: 0,
  },
  titleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
  },
  countBubble: {
    minWidth: 24,
    height: 24,
    borderRadius: 999,
    px: 0.75,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'error.main',
    color: 'neutral.25',
    typography: 'body2',
    fontWeight: 800,
  },
  headerTitle: {
    color: 'background.default',
    typography: 'h4',
    fontWeight: 800,
    lineHeight: 1,
  },
  headerTitleIcon: {
    color: 'background.default',
    fontSize: 22,
    flexShrink: 0,
  },
} as const;

type NeobetBetslipHeaderProps = {
  controller: NeobetBetslipDrawerController;
  isMobile: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
};

const NeobetBetslipHeader = ({ controller, isMobile, collapsed, onToggleCollapse }: NeobetBetslipHeaderProps) => {
  const { t } = useLocalization();
  const {
    bets,
    selectedWallet,
    localizedWalletTypeOptions,
    handleWalletChange,
    handleMenuChange,
    selectedMenuItem,
    selectedPlayer,
    setSelectedPlayer,
    closeBetslipDrawer,
    isWaysTicket,
    isAgent,
  } = controller;

  const { badgeRef, flyAnimation } = useBetslipFlyAnimation(bets.length, !isMobile);

  const isBankersActive = selectedMenuItem === 'bankers';
  const isBankersDisabled = isWaysTicket || bets.length < 3;
  const walletTriggerLabel =
    localizedWalletTypeOptions.find((option) => option.value === selectedWallet)?.label ?? selectedWallet;
  const activeBalanceType = selectedWallet === WALLET_TYPE.BONUS ? WALLET_TYPE.BONUS : WALLET_TYPE.MAIN;

  return (
    <>
      <Box sx={styles.headerTopRow}>
        <Stack direction='row' sx={styles.headerTitleRow}>
          <Box sx={styles.titleBadge}>
            <Article sx={styles.headerTitleIcon} />
            <Typography sx={styles.headerTitle}>{t('betslip', 'Betslip')}</Typography>
            <Box ref={badgeRef} sx={styles.countBubble}>
              {bets.length}
            </Box>
          </Box>
          {isMobile && (
            <IconButton onClick={closeBetslipDrawer} size='small' sx={styles.headerCloseButton}>
              <ExpandMore />
            </IconButton>
          )}
          {!isMobile && onToggleCollapse && (
            <IconButton
              onClick={onToggleCollapse}
              size='small'
              sx={styles.headerCloseButton}
              data-testid='neobet-betslip-desktop-collapse-toggle'
            >
              {collapsed ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Stack>
      </Box>

      {!collapsed && (
        <>
          {isAgent && <UserSelect selectedUser={selectedPlayer} onUserSelect={setSelectedPlayer} />}

          <Box sx={styles.controlsSection} data-testid='neobet-betslip-header-controls'>
            <Box sx={styles.controlsRow}>
              {localizedWalletTypeOptions.length > 0 && (
                <NeobetBetslipMenu
                  selectedMenuItem={selectedWallet}
                  onChange={handleWalletChange}
                  options={localizedWalletTypeOptions}
                  ariaLabel={walletTriggerLabel}
                  renderValue={() => (
                    <NeobetBetslipMenuTriggerValue
                      icon={<AccountBalanceWalletOutlinedIcon sx={styles.headerTriggerIcon} />}
                      label={walletTriggerLabel}
                    >
                      <FormattedBalance balanceType={activeBalanceType} sx={styles.walletTriggerBalance} />
                    </NeobetBetslipMenuTriggerValue>
                  )}
                  sx={styles.headerMenu}
                />
              )}

              <Button
                type='button'
                sx={styles.bankerToggle(isBankersActive)}
                disabled={isBankersDisabled}
                aria-pressed={isBankersActive}
                aria-label={t('bankers', 'Bankers')}
                onClick={() => handleMenuChange(isBankersActive ? 'normal' : 'bankers')}
              >
                B
              </Button>
            </Box>
          </Box>
        </>
      )}
      {flyAnimation ? (
        <Box
          key={`fly-${flyAnimation.key}`}
          sx={headerStyles.mobileBetslipFabFlyDot(flyAnimation.origin, flyAnimation.target)}
          data-testid='neobet-betslip-header-fly-dot'
        />
      ) : null}
    </>
  );
};

export default NeobetBetslipHeader;
